import { observer, useLocalObservable } from 'mobx-react';
import { useEffect, useRef, useState } from 'react';
import { autorun, runInAction } from 'mobx';
import {
  getErrorFields,
  PROFILER_INDUSTRY_CODES_SEARCH_FIELDS,
  PROFILER_SEARCH_CATEGORY_FIELDS,
  PROFILER_SEARCH_FIELDS,
  trimStateFields
} from '../../../../utils/constants/fields';
import {
  clearDistanceWhenCityCleared,
  getFilledSearchCategoriesCount,
  mapFieldsToState
} from '../../../../utils/utils';
import { paths } from '../../../../utils/constants/routes';
import {
  PROFILER_INDUSTRY_CODES_SEARCH_CATEGORIES,
  PROFILER_SEARCH_CATEGORIES
} from '../../../../utils/constants/searchCategories';
import { UI_OPTIONS } from '../../../../utils/constants/uiOptions';
import { UI_OPTION_KEYS } from '../../../../utils/constants/uiOptionKeys';
import { toggleIndustryCodeSelection } from '../../../../utils/profiler/toggleIndustryCodeSelection';
import { collapseIndustryCodes } from '../../../../utils/profiler/collapseIndustryCodes';
import { useStore } from '../../../../store/store';
import useHistory from '../../../../hooks/useHistory';
import { OPTION_KEYS } from '../../../../utils/constants/optionKeys';
import pubSub from '../../../../utils/pubsub';
import {
  eventSearchTypeChanged,
  eventSearchAdvancedExpanded,
  eventSearchExecuted
} from '../../../../ga4/ga4';
import {
  buildCharacteristicsSearchParams,
  buildIndustriesSearchParams
} from '../../../../ga4/searchEventParams';
import Radio from '../../../../components/inputs/Radio';
import MultiSelect from '../../../../components/inputs/MultiSelect';
import IconButton, { ICON_BUTTON_ICONS } from '../../../../components/buttons/IconButton';
import { getProfilerCompanyCriteriaSearchFields } from '../agent/constants/profilerCompanyCriteriaSearchFields';
import { getProfilerIndustryCodesSearchFields } from '../agent/constants/profilerIndustryCodesSearchFields';
import ProfilerCompanyCriteriaSearchFormDesktop from '../agent/ProfilerCompanyCriteriaSearchFormDesktop';
import ProfilerCompanyCriteriaSearchFormMobile from '../agent/ProfilerCompanyCriteriaSearchFormMobile';
import ProfilerIndustryCodesSearchFormDesktop from '../agent/ProfilerIndustryCodesSearchFormDesktop';
import ProfilerIndustryCodesSearchFormMobile from '../agent/ProfilerIndustryCodesSearchFormMobile';
import ProfilerCompanyBulkSearchFormDesktop from '../agent/ProfilerCompanyBulkSearchFormDesktop';
import ProfilerCompanyBulkSearchFormMobile from '../agent/ProfilerCompanyBulkSearchFormMobile';
import useBulkCompanySearch from '../../../../hooks/useBulkCompanySearch';
import BulkUploadReview from './BulkUploadReview';
import SearchFormMobileSection from '../agent/SearchFormMobileSection';
import CompanyNameSearch from './company-name/CompanyNameSearch';
import ProfilerDemoMessage from './shared/ProfilerDemoMessage';
import getErroredSections from '../../utils/getErroredSections';
import {
  clearOwnershipBasisWithoutStructures,
  isOwnershipBasisMissing
} from '../../../../utils/ownershipCriteria';

/**
 * ProfilerSearch - Self-contained Profiler search component
 *
 * Manages all profiler search state:
 * - Category navigation (Characteristics/Industries/Name/Bulk)
 * - Characteristics search fields and validation
 * - Industry codes search with tree selection
 * - Mobile filters display
 * - How-to popup
 *
 * @param {React.ReactNode} tabs - Tab navigation component from parent
 * @param {React.ReactNode} searchTypeInput - Search type input for mobile
 * @param {function} onHowToPopupToggle - Callback when how-to popup is toggled
 * @param {function} onMobileFilterChange - Callback when mobile filter state changes
 * @param {string} initialSearchQuery - TD-1709: Initial search query from URL parameter (for restoration)
 * @param {string} initialCategory - TD-1709: Initial category from URL parameter (for restoration)
 */
const ProfilerSearch = observer(
  ({
    tabs,
    searchTypeInput,
    onHowToPopupToggle,
    onMobileFilterChange,
    initialSearchQuery = '',
    initialCategory = ''
  }) => {
    const { profilerAccessStore, utilsStore } = useStore();
    const { navigate } = useHistory();
    const isRenderedRef = useRef(true);

    // TD-1709: Resolve initial category from URL param or default to Characteristics
    const resolveInitialCategory = () => {
      if (initialCategory) {
        // Find the category value that matches the URL param (case-insensitive)
        const categories = UI_OPTIONS[UI_OPTION_KEYS.PROFILER_SEARCH_CATEGORY];
        const matchingEntry = Object.entries(categories).find(
          ([name]) => name.toLowerCase() === initialCategory.toLowerCase()
        );
        if (matchingEntry) {
          return matchingEntry[1]; // Return the value (e.g., 'companyName')
        }
      }
      return UI_OPTIONS[UI_OPTION_KEYS.PROFILER_SEARCH_CATEGORY]['Characteristics'];
    };

    const state = useLocalObservable(() => ({
      // Profiler-Lite role bridged onto the search state so the criteria-field
      // builders can drop the EBIT-margin range for Lite (no EBIT data server-side).
      get isProfilerLiteOnly() {
        return profilerAccessStore.isProfilerLiteOnly;
      },
      // ============== Category State ==============
      categoryFields: {
        [PROFILER_SEARCH_CATEGORY_FIELDS.SEARCH_CATEGORY.NAME]: [resolveInitialCategory()]
      },
      setCategoryFieldValue: (field = {}, value) => {
        const prevCategoryValue = state.categoryFields[field.NAME];
        state.categoryFields[field.NAME] = value;
        // TD-1709: Update URL with category (replaceState to avoid history pollution)
        if (field.NAME === PROFILER_SEARCH_CATEGORY_FIELDS.SEARCH_CATEGORY.NAME && value?.[0]) {
          const categories = UI_OPTIONS[UI_OPTION_KEYS.PROFILER_SEARCH_CATEGORY];
          // Find the display name for the selected value
          const categoryName = Object.entries(categories).find(([, v]) => v === value[0])?.[0];
          if (categoryName) {
            const params = new URLSearchParams(globalThis.location.search);
            params.set('cat', categoryName);
            const newUrl = `${globalThis.location.pathname}?${params.toString()}`;
            globalThis.history.replaceState({}, '', newUrl);
          }
          const fromName = Object.entries(categories).find(
            ([, v]) => v === prevCategoryValue?.[0]
          )?.[0];
          if (categoryName && fromName && categoryName !== fromName) {
            eventSearchTypeChanged('corporates', fromName, categoryName);
          }
        }
      },
      get categories() {
        return Object.entries(UI_OPTIONS[UI_OPTION_KEYS.PROFILER_SEARCH_CATEGORY]).map(
          ([name, value]) => ({ name, value })
        );
      },
      get selectedCategory() {
        return state.categoryFields[PROFILER_SEARCH_CATEGORY_FIELDS.SEARCH_CATEGORY.NAME][0];
      },

      // ============== Layout State ==============
      get layout() {
        return utilsStore.windowWidth < 1201 ? 'mobile' : 'desktop';
      },

      // ============== Characteristics State ==============
      characteristicsMobileFiltersDisplayed: false,
      toggleCharacteristicsMobileFilters: () => {
        state.characteristicsMobileFiltersDisplayed = !state.characteristicsMobileFiltersDisplayed;
        const savedScrollY = state.characteristicsSavedScrollY;
        state.characteristicsSavedScrollY = 0;
        setTimeout(() => window.scrollTo({ top: savedScrollY }), 0);
        onMobileFilterChange?.(state.characteristicsMobileFiltersDisplayed);
      },
      characteristicsSavedScrollY: 0,
      characteristicsMobileFilterSection: null,
      setCharacteristicsMobileFilterSection: (value) => {
        state.characteristicsSavedScrollY = window.scrollY;
        state.characteristicsMobileFilterSection = value;
        state.characteristicsMobileFiltersDisplayed = true;
        onMobileFilterChange?.(true);
      },

      characteristicsFields: {
        ...mapFieldsToState(PROFILER_SEARCH_FIELDS),
        [PROFILER_SEARCH_FIELDS.SEARCH_TYPE.NAME]: [
          UI_OPTIONS[UI_OPTION_KEYS.SEARCH_TYPE].Profiler
        ],
        ...(utilsStore.getProfilerSearchCriteriaPreFillFields() || {})
      },
      setCharacteristicsFieldValue: (field = {}, value) => {
        state.characteristicsFields[field.NAME] = value;
        clearDistanceWhenCityCleared(
          state.characteristicsFields,
          field,
          value,
          PROFILER_SEARCH_FIELDS.CITY_ZIPCODE,
          PROFILER_SEARCH_FIELDS.DISTANCE
        );
        if (
          !state.characteristicsFields[PROFILER_SEARCH_FIELDS.CAGR_MIN.NAME] &&
          !state.characteristicsFields[PROFILER_SEARCH_FIELDS.CAGR_MAX.NAME]
        ) {
          state.characteristicsFields[PROFILER_SEARCH_FIELDS.CAGR_PERIOD.NAME] = [];
        }
        clearOwnershipBasisWithoutStructures(state.characteristicsFields);
      },
      get characteristicsFieldsCount() {
        return getFilledSearchCategoriesCount(
          state.characteristicsFields,
          PROFILER_SEARCH_CATEGORIES,
          PROFILER_SEARCH_FIELDS
        );
      },
      onCharacteristicsSubmitErrorState: false,
      setCharacteristicsOnSubmitErrorState: (value = false) =>
        (state.onCharacteristicsSubmitErrorState = value),
      get characteristicsValidationFields() {
        return getErrorFields(Object.values(PROFILER_SEARCH_FIELDS), state.characteristicsFields);
      },
      get isCharacteristicsSearchDisabled() {
        return (
          !!state.characteristicsValidationFields.invalidFields.filter((f) => !f.isOnSubmit)
            .length ||
          (state.onCharacteristicsSubmitErrorState &&
            !!state.characteristicsValidationFields.invalidFields.length) ||
          state.isCharacteristicsSearchFieldsDisabled ||
          (state.characteristicsFields[PROFILER_SEARCH_FIELDS.CAGR_MIN.NAME] &&
            !state.characteristicsFields[PROFILER_SEARCH_FIELDS.CAGR_PERIOD.NAME].length) ||
          isOwnershipBasisMissing(state.characteristicsFields) ||
          (state.characteristicsFields[PROFILER_SEARCH_FIELDS.CITY_ZIPCODE.NAME]?.trim() &&
            !(state.characteristicsFields[PROFILER_SEARCH_FIELDS.HEADQUARTER.NAME]?.length > 0)) ||
          (state.characteristicsFields[PROFILER_SEARCH_FIELDS.DISTANCE.NAME] &&
            !state.characteristicsFields[PROFILER_SEARCH_FIELDS.CITY_ZIPCODE.NAME]?.trim())
        );
      },
      get isCharacteristicsSearchFieldsDisabled() {
        return (
          (profilerAccessStore.isProfilerDemo && profilerAccessStore.demoSearchExceededLimit) ||
          !profilerAccessStore.canUseProfilerSearch
        );
      },
      isCharacteristicsAdvancedFieldsToggled: false,
      toggleCharacteristicsAdvancedFields: () => {
        state.isCharacteristicsAdvancedFieldsToggled =
          !state.isCharacteristicsAdvancedFieldsToggled;
        if (state.isCharacteristicsAdvancedFieldsToggled) {
          eventSearchAdvancedExpanded('corporates', 'Characteristics');
        }
      },

      // Compatibility layer for field builders
      get profilerCompanyCriteriaFields() {
        return state.characteristicsFields;
      },
      get setProfilerCompanyCriteriaFieldValue() {
        return state.setCharacteristicsFieldValue;
      },
      get profilerCompanyCriteriaValidationFields() {
        return state.characteristicsValidationFields;
      },
      get onProfilerCompanyCriteriaSubmitErrorState() {
        return state.onCharacteristicsSubmitErrorState;
      },

      // ============== Industry Codes State ==============
      industryCodesMobileFiltersDisplayed: false,
      toggleIndustryCodesMobileFilters: () => {
        state.industryCodesMobileFiltersDisplayed = !state.industryCodesMobileFiltersDisplayed;
        const savedScrollY = state.industryCodesSavedScrollY;
        state.industryCodesSavedScrollY = 0;
        setTimeout(() => window.scrollTo({ top: savedScrollY }), 0);
        onMobileFilterChange?.(state.industryCodesMobileFiltersDisplayed);
      },
      industryCodesSavedScrollY: 0,
      industryCodesMobileFilterSection: null,
      setIndustryCodesMobileFilterSection: (value) => {
        state.industryCodesSavedScrollY = window.scrollY;
        state.industryCodesMobileFilterSection = value;
        state.industryCodesMobileFiltersDisplayed = true;
        onMobileFilterChange?.(true);
      },

      industryCodesFields: {
        ...mapFieldsToState(PROFILER_INDUSTRY_CODES_SEARCH_FIELDS),
        [PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.SEARCH_TYPE.NAME]: [
          UI_OPTIONS[UI_OPTION_KEYS.SEARCH_TYPE].Profiler
        ]
      },
      setIndustryCodesFieldValue: (field = {}, value) => {
        state.industryCodesFields[field.NAME] = value;
        if (
          !state.industryCodesFields[PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.CAGR_MIN.NAME] &&
          !state.industryCodesFields[PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.CAGR_MAX.NAME]
        ) {
          state.industryCodesFields[PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.CAGR_PERIOD.NAME] = [];
        }
        clearOwnershipBasisWithoutStructures(state.industryCodesFields);
        clearDistanceWhenCityCleared(
          state.industryCodesFields,
          field,
          value,
          PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.CITY_ZIPCODE,
          PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.DISTANCE
        );
      },
      get industryCodesFieldsCount() {
        return getFilledSearchCategoriesCount(
          state.industryCodesFields,
          PROFILER_INDUSTRY_CODES_SEARCH_CATEGORIES,
          PROFILER_INDUSTRY_CODES_SEARCH_FIELDS
        );
      },
      onIndustryCodesSubmitErrorState: false,
      setIndustryCodesOnSubmitErrorState: (value = false) =>
        (state.onIndustryCodesSubmitErrorState = value),
      get industryCodesValidationFields() {
        return getErrorFields(
          Object.values(PROFILER_INDUSTRY_CODES_SEARCH_FIELDS),
          state.industryCodesFields
        );
      },
      get isIndustryCodesSearchDisabled() {
        return (
          !!state.industryCodesValidationFields.invalidFields.filter((f) => !f.isOnSubmit).length ||
          (state.onIndustryCodesSubmitErrorState &&
            !!state.industryCodesValidationFields.invalidFields.length) ||
          state.isIndustryCodesSearchFieldsDisabled ||
          (state.industryCodesFields[PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.CAGR_MIN.NAME] &&
            !state.industryCodesFields[PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.CAGR_PERIOD.NAME]
              .length) ||
          isOwnershipBasisMissing(state.industryCodesFields) ||
          (state.industryCodesFields[
            PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.CITY_ZIPCODE.NAME
          ]?.trim() &&
            !(
              state.industryCodesFields[PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.HEADQUARTER.NAME]
                ?.length > 0
            )) ||
          (state.industryCodesFields[PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.DISTANCE.NAME] &&
            !state.industryCodesFields[
              PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.CITY_ZIPCODE.NAME
            ]?.trim())
        );
      },
      get isIndustryCodesSearchFieldsDisabled() {
        return profilerAccessStore.isProfilerDemo && profilerAccessStore.demoSearchExceededLimit;
      },
      isIndustryCodesAdvancedFieldsToggled: false,
      toggleIndustryCodesAdvancedFields: () => {
        state.isIndustryCodesAdvancedFieldsToggled = !state.isIndustryCodesAdvancedFieldsToggled;
        if (state.isIndustryCodesAdvancedFieldsToggled) {
          eventSearchAdvancedExpanded('corporates', 'Industries');
        }
      },

      // Industry codes tree state
      searchTreeToggled: false,
      toggleSearchTree: () => {
        state.searchTreeToggled = !state.searchTreeToggled;
        if (!state.searchTreeToggled) {
          state.setSearchKey('');
        }
      },
      searchIsTyping: false,
      searchKey: '',
      setSearchKey: (value = '') => {
        state.searchIsTyping = true;
        state.searchKey = value;
        clearTimeout(state.searchInputTimeout);
        if (value.length < 1) {
          return;
        }
        state.searchInputTimeout = setTimeout(
          ((firedSearchKey = '') => {
            return () => {
              if (!isRenderedRef.current || state.searchKey !== firedSearchKey) {
                return;
              }
              runInAction(() => {
                const nodeMap = new Map(state.industryCodesFlat.map((node) => [node.value, node]));
                state.industryCodesFlat.forEach((node) => {
                  node.toggled = false;
                });
                const allMatches = state.industryCodesFlat.filter((node) =>
                  node.name.toLowerCase().includes(firedSearchKey.toLowerCase())
                );
                const processedParents = new Set();
                allMatches.forEach((match) => {
                  if (match.children?.length) {
                    match.toggled = true;
                  }
                  let current = match;
                  while (current.parent) {
                    const parent = nodeMap.get(current.parent.value);
                    if (!parent) break;
                    if (!processedParents.has(parent.value)) {
                      parent.toggled = true;
                      processedParents.add(parent.value);
                    }
                    current = parent;
                  }
                });
                state.searchIsTyping = false;
                state.searchResults = allMatches.map((m) => m.value);
              });
            };
          })(value),
          500
        );
      },
      searchInputTimeout: null,
      searchResults: [],
      industryCodesFlat: [],
      industryCodesRaw: [],

      toggleIndustryCode: (code) => {
        const fromFlat = state.industryCodesFlat.find((_c) => _c.value === code.value);
        runInAction(() => {
          fromFlat.toggled = !fromFlat.toggled;
        });
      },
      selectIndustryCode: (code) => {
        runInAction(() => {
          toggleIndustryCodeSelection(state.industryCodesFlat, code);
        });
      },
      toggleAllIndustryCodes: () => {
        runInAction(() => {
          state.industryCodesFlat.forEach((code) => {
            if (code.children?.length) {
              code.toggled = true;
            }
          });
        });
      },
      untoggleAllIndustryCodes: () => {
        runInAction(() => {
          state.industryCodesFlat.forEach((code) => {
            code.toggled = false;
          });
        });
      },
      clearAllIndustryCodes: () => {
        runInAction(() => {
          state.industryCodesFlat.forEach((code) => {
            code.selected = false;
          });
        });
      },

      // Compatibility layer for field builders
      get profilerIndustryCodesFields() {
        return state.industryCodesFields;
      },
      get setProfilerIndustryCodesFieldValue() {
        return state.setIndustryCodesFieldValue;
      },
      get profilerIndustryCodesValidationFields() {
        return state.industryCodesValidationFields;
      },
      get onProfilerIndustryCodesSubmitErrorState() {
        return state.onIndustryCodesSubmitErrorState;
      },

      // ============== Shared State ==============
      get disableProfilerSearchSelectionMobile() {
        return !profilerAccessStore.canUseProfilerSearch;
      }
    }));

    // Initialize industry codes from options
    useEffect(() => {
      if (utilsStore.options) {
        runInAction(() => {
          const industryCodesCopy =
            utilsStore.options[OPTION_KEYS.PROFILER_INDUSTRY_CODES].getInitialListsCopy();
          state.industryCodesFlat = industryCodesCopy.flatList;
          state.industryCodesRaw = industryCodesCopy.rawList;
        });
      }
    }, [utilsStore.options, state]);

    // Sync selected industry codes to field.
    // INVARIANT: the field holds the minimal COVERING SET, never the expansion —
    // the fully expanded selection lives only in `industryCodesFlat`. Everything
    // downstream (results URL, request body, currentSearch, GA4) reads the field,
    // and the expansion is what pushed the results URL past CloudFront's 8192-byte
    // limit and past GA4's 100-char parameter cap.
    useEffect(() => {
      const dispose = autorun(() => {
        const selected = collapseIndustryCodes(
          state.industryCodesFlat.filter((code) => code.selected).map((code) => code.value)
        );
        runInAction(() => {
          state.setIndustryCodesFieldValue(
            PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.INDUSTRY_CODES,
            selected
          );
        });
      });
      return () => dispose();
    }, [state]);

    // Handle click outside to close search tree
    useEffect(() => {
      const handleClickOutside = (event) => {
        const targetElement = event.target;
        const searchInputWrapper = document.querySelector(
          '.search-key-input-wrapper-industry-codes'
        );
        if (searchInputWrapper && !searchInputWrapper.contains(targetElement)) {
          if (state.searchTreeToggled) {
            state.toggleSearchTree();
          }
        }
      };
      const onMultiselectInterceptorClick = (payload) => {
        if (payload?.event === 'multiselect-click' && state.searchTreeToggled) {
          state.toggleSearchTree();
        }
      };
      document.addEventListener('click', handleClickOutside, { capture: true });
      pubSub.subscribe(onMultiselectInterceptorClick);
      return () => {
        document.removeEventListener('click', handleClickOutside, { capture: true });
        pubSub.unsubscribe(onMultiselectInterceptorClick);
      };
    }, [state]);

    // Cleanup on unmount
    useEffect(() => {
      return () => {
        isRenderedRef.current = false;
      };
    }, []);

    // Bulk company upload search (TD-2344): real backend — upload -> poll -> review -> finalize.
    // File lives here (not inside the forms) so a failed attempt survives the form remount.
    const [bulkFile, setBulkFile] = useState(null);
    const bulk = useBulkCompanySearch();
    const bulkResults = bulk.status === 'completed' ? bulk.results : null;
    const bulkLoading = bulk.status === 'uploading' || bulk.status === 'pending';
    // TD-2660: a soft poll 'timeout' keeps the searchId (the worker may still be matching a large
    // upload). Show a resume affordance instead of silently dropping back to the upload form — a
    // completed run has already auto-finalized its results on the BE, so resuming surfaces them.
    const bulkTimedOut = bulk.status === 'timeout' && !!bulk.searchId;
    const submitBulk = (file) => {
      if (!file || profilerAccessStore.isProfilerDemo) {
        return;
      }
      bulk.start(file);
    };
    const resetBulk = () => {
      bulk.reset();
      setBulkFile(null);
    };

    // Finalize handoff (TD-2344): the review step promotes the picks and hands back the searchId;
    // open the finalized set in the standard results view — same URL shape as executeSearch. At the
    // review render site state.selectedCategory is already 'companyBulkSearch' (the Bulk branch guard).
    const onBulkFinalized = (finalizedSearchId) => {
      if (!finalizedSearchId) {
        return;
      }
      navigate(
        paths.SEARCH_RESULTS +
          `?${PROFILER_SEARCH_CATEGORY_FIELDS.SEARCH_CATEGORY.NAME}=${encodeURIComponent(
            state.selectedCategory
          )}&searchId=${encodeURIComponent(finalizedSearchId)}&page=1`
      );
    };

    // Search execution
    const executeSearch = () => {
      if (
        state.selectedCategory ===
        UI_OPTIONS[UI_OPTION_KEYS.PROFILER_SEARCH_CATEGORY]['Characteristics']
      ) {
        trimStateFields(state.characteristicsFields);
        if (state.characteristicsValidationFields.invalidFields.length) {
          if (!state.onCharacteristicsSubmitErrorState) {
            state.setCharacteristicsOnSubmitErrorState(true);
          }
          return;
        }
        eventSearchExecuted({
          ...buildCharacteristicsSearchParams(state.characteristicsFields),
          advanced_criteria_expanded: state.isCharacteristicsAdvancedFieldsToggled
        });
        const fields = Object.entries(state.characteristicsFields)
          .map(([k, v]) => `${[k]}=${encodeURIComponent(JSON.stringify(v))}`)
          .join('&');
        navigate(
          paths.SEARCH_RESULTS +
            `?${PROFILER_SEARCH_CATEGORY_FIELDS.SEARCH_CATEGORY.NAME}=${encodeURIComponent(
              state.selectedCategory
            )}&page=1&${fields}`
        );
      } else if (
        state.selectedCategory === UI_OPTIONS[UI_OPTION_KEYS.PROFILER_SEARCH_CATEGORY]['Industries']
      ) {
        trimStateFields(state.industryCodesFields);
        if (state.industryCodesValidationFields.invalidFields.length) {
          if (!state.onIndustryCodesSubmitErrorState) {
            state.setIndustryCodesOnSubmitErrorState(true);
          }
          return;
        }
        eventSearchExecuted({
          ...buildIndustriesSearchParams(state.industryCodesFields),
          advanced_criteria_expanded: state.isIndustryCodesAdvancedFieldsToggled
        });
        const fields = Object.entries(state.industryCodesFields)
          .map(([k, v]) => `${[k]}=${encodeURIComponent(JSON.stringify(v))}`)
          .join('&');
        navigate(
          paths.SEARCH_RESULTS +
            `?${PROFILER_SEARCH_CATEGORY_FIELDS.SEARCH_CATEGORY.NAME}=${encodeURIComponent(
              state.selectedCategory
            )}&page=1&${fields}`
        );
      }
    };

    const onSearch = (e) => {
      e?.preventDefault?.();
      executeSearch();
    };

    // Build field components
    const characteristicsFieldComponents = getProfilerCompanyCriteriaSearchFields(state);
    const industryCodesFieldComponents = getProfilerIndustryCodesSearchFields(state);

    // Compute errored sections
    const characteristicsErroredSections = getErroredSections({
      categories: PROFILER_SEARCH_CATEGORIES,
      fieldsConfig: PROFILER_SEARCH_FIELDS,
      hasSubmitError: state.onCharacteristicsSubmitErrorState,
      validationFields: state.characteristicsValidationFields
    });

    const industryCodesErroredSections = getErroredSections({
      categories: PROFILER_INDUSTRY_CODES_SEARCH_CATEGORIES,
      fieldsConfig: PROFILER_INDUSTRY_CODES_SEARCH_FIELDS,
      hasSubmitError: state.onIndustryCodesSubmitErrorState,
      validationFields: state.industryCodesValidationFields
    });

    // Mobile filter section view - Characteristics
    if (state.characteristicsMobileFiltersDisplayed) {
      return (
        <SearchFormMobileSection
          section={state.characteristicsMobileFilterSection}
          onBack={state.toggleCharacteristicsMobileFilters}
          fields={characteristicsFieldComponents}
          hideMandatoryHint
        />
      );
    }

    // Mobile filter section view - Industry Codes
    if (state.industryCodesMobileFiltersDisplayed) {
      return (
        <SearchFormMobileSection
          section={state.industryCodesMobileFilterSection}
          onBack={state.toggleIndustryCodesMobileFilters}
          fields={industryCodesFieldComponents}
          hideMandatoryHint
        />
      );
    }

    return (
      <>
        {/* How-to popup icon (hidden during the bulk review, which has its own tab help) */}
        {profilerAccessStore.canUseProfilerSearch && !bulkResults && (
          <IconButton
            id="profiler-how-to-search-popup-icon"
            icon={ICON_BUTTON_ICONS.QUESTION_CIRCLE}
            withBorder={false}
            withFill={false}
            onClick={onHowToPopupToggle}
          />
        )}

        {/* Characteristics search */}
        {state.selectedCategory ===
          UI_OPTIONS[UI_OPTION_KEYS.PROFILER_SEARCH_CATEGORY]['Characteristics'] && (
          <>
            <ProfilerCompanyCriteriaSearchFormDesktop
              fields={characteristicsFieldComponents}
              isSearchDisabled={state.isCharacteristicsSearchDisabled}
              onSearch={onSearch}
              tabs={tabs}
              categoryFields={state.categoryFields}
              setCategoryFieldValue={state.setCategoryFieldValue}
              categoryOptions={state.categories}
              isAdvancedFieldsToggled={state.isCharacteristicsAdvancedFieldsToggled}
              toggleAdvancedFields={state.toggleCharacteristicsAdvancedFields}
            />
            <ProfilerCompanyCriteriaSearchFormMobile
              searchTypeInput={searchTypeInput}
              onShowSection={(section) => state.setCharacteristicsMobileFilterSection(section)}
              fieldsCount={state.characteristicsFieldsCount}
              isSearchDisabled={state.isCharacteristicsSearchDisabled}
              onSearch={onSearch}
              fields={characteristicsFieldComponents}
              erroredSections={characteristicsErroredSections}
              categoryFields={state.categoryFields}
              setCategoryFieldValue={state.setCategoryFieldValue}
              categoryOptions={state.categories.filter(({ disabled }) => !disabled)}
            />
          </>
        )}

        {/* Industries search */}
        {state.selectedCategory ===
          UI_OPTIONS[UI_OPTION_KEYS.PROFILER_SEARCH_CATEGORY]['Industries'] && (
          <>
            {state.layout === 'desktop' ? (
              <ProfilerIndustryCodesSearchFormDesktop
                fields={industryCodesFieldComponents}
                isSearchDisabled={state.isIndustryCodesSearchDisabled}
                onSearch={onSearch}
                tabs={tabs}
                categoryFields={state.categoryFields}
                setCategoryFieldValue={state.setCategoryFieldValue}
                categoryOptions={state.categories}
                searchTreeToggled={state.searchTreeToggled}
                toggleSearchTree={state.toggleSearchTree}
                isTyping={state.searchIsTyping}
                searchKey={state.searchKey}
                setSearchKey={state.setSearchKey}
                industryCodesFlat={state.industryCodesFlat}
                industryCodesRaw={state.industryCodesRaw}
                toggleIndustryCode={state.toggleIndustryCode}
                selectIndustryCode={state.selectIndustryCode}
                toggleAll={state.toggleAllIndustryCodes}
                untoggleAll={state.untoggleAllIndustryCodes}
                clearAll={state.clearAllIndustryCodes}
                isAdvancedFieldsToggled={state.isIndustryCodesAdvancedFieldsToggled}
                toggleAdvancedFields={state.toggleIndustryCodesAdvancedFields}
              />
            ) : (
              <ProfilerIndustryCodesSearchFormMobile
                searchTypeInput={searchTypeInput}
                onShowSection={(section) => state.setIndustryCodesMobileFilterSection(section)}
                fieldsCount={state.industryCodesFieldsCount}
                isSearchDisabled={state.isIndustryCodesSearchDisabled}
                onSearch={onSearch}
                fields={industryCodesFieldComponents}
                erroredSections={industryCodesErroredSections}
                categoryFields={state.categoryFields}
                setCategoryFieldValue={state.setCategoryFieldValue}
                categoryOptions={state.categories.filter(({ disabled }) => !disabled)}
                searchTreeToggled={state.searchTreeToggled}
                toggleSearchTree={state.toggleSearchTree}
                isTyping={state.searchIsTyping}
                searchKey={state.searchKey}
                setSearchKey={state.setSearchKey}
                industryCodesFlat={state.industryCodesFlat}
                industryCodesRaw={state.industryCodesRaw}
                toggleIndustryCode={state.toggleIndustryCode}
                selectIndustryCode={state.selectIndustryCode}
                toggleAll={state.toggleAllIndustryCodes}
                untoggleAll={state.untoggleAllIndustryCodes}
                clearAll={state.clearAllIndustryCodes}
              />
            )}
          </>
        )}

        {/* Company name search */}
        {state.selectedCategory === UI_OPTIONS[UI_OPTION_KEYS.PROFILER_SEARCH_CATEGORY]['Name'] && (
          <>
            {state.layout === 'desktop' ? (
              <div className="formform desktop">
                <div className="fields-area">
                  {tabs}
                  <div className="row category-group">
                    <Radio
                      field={PROFILER_SEARCH_CATEGORY_FIELDS.SEARCH_CATEGORY}
                      value={
                        state.categoryFields[PROFILER_SEARCH_CATEGORY_FIELDS.SEARCH_CATEGORY.NAME]
                      }
                      setFieldValue={state.setCategoryFieldValue}
                      values={state.categories}
                    />
                  </div>
                  <div className="flip-horizontal-fields-area" style={{ minHeight: '320px' }}>
                    <ProfilerDemoMessage />
                    <CompanyNameSearch
                      inputId="ps-input-searchKey"
                      initialSearchQuery={initialSearchQuery}
                    />
                  </div>
                </div>
              </div>
            ) : (
              <div className="formform mobile">
                <ProfilerDemoMessage />
                <div className="mobile-search-form-title">Select search type of preference</div>
                {searchTypeInput}
                <MultiSelect
                  field={PROFILER_SEARCH_CATEGORY_FIELDS.SEARCH_CATEGORY_MOBILE}
                  value={
                    state.categoryFields[
                      PROFILER_SEARCH_CATEGORY_FIELDS.SEARCH_CATEGORY_MOBILE.NAME
                    ]
                  }
                  setFieldValue={state.setCategoryFieldValue}
                  values={state.categories.filter(({ disabled }) => !disabled)}
                />
                <CompanyNameSearch
                  inputId="ps-input-searchKey"
                  initialSearchQuery={initialSearchQuery}
                />
              </div>
            )}
          </>
        )}

        {/* Bulk search */}
        {state.selectedCategory === UI_OPTIONS[UI_OPTION_KEYS.PROFILER_SEARCH_CATEGORY]['Bulk'] && (
          <>
            {bulkResults && (
              <BulkUploadReview
                results={bulkResults}
                searchId={bulk.searchId}
                onBack={resetBulk}
                onFinalized={onBulkFinalized}
              />
            )}
            {bulkTimedOut && (
              // Mirror BulkUploadReview's structure (summary + footer) INSIDE a .bulk-review
              // wrapper — the summary/counts/back/search-btn-wrap rules are all nested under
              // .bulk-review in search-form.less, so a bare sibling would render unstyled.
              <div className="bulk-review">
                <div className="bulk-review-summary">
                  <button type="button" className="bulk-back" onClick={resetBulk}>
                    &larr; Upload a different file
                  </button>
                  {/* role="status" on the message itself, not the wrapper: the wrapper is
                      aria-atomic, so it would read the "Upload a different file" control out as
                      part of the status. Matches the convention elsewhere (DiscoverContactsPanel,
                      DiscoveredContactsSection, MfaSetupForm). */}
                  <div className="bulk-counts" role="status">
                    This upload is taking longer than usual — it may still be matching. We&rsquo;ve
                    kept your search, so you don&rsquo;t need to upload again.
                  </div>
                </div>
                <div className="bulk-review-footer">
                  <div className="search-btn-wrap">
                    <button
                      type="button"
                      className="btn btn-primary disable-fade-primary"
                      onClick={bulk.resume}
                    >
                      Keep checking
                    </button>
                  </div>
                </div>
              </div>
            )}
            {!bulkResults && !bulkTimedOut && (
              <>
                <ProfilerCompanyBulkSearchFormDesktop
                  onSearch={submitBulk}
                  file={bulkFile}
                  onFileChange={setBulkFile}
                  tabs={tabs}
                  categoryFields={state.categoryFields}
                  setCategoryFieldValue={state.setCategoryFieldValue}
                  categoryOptions={state.categories}
                  loading={bulkLoading}
                  progress={bulk.progress}
                />
                <ProfilerCompanyBulkSearchFormMobile
                  onSearch={submitBulk}
                  file={bulkFile}
                  onFileChange={setBulkFile}
                  searchTypeInput={searchTypeInput}
                  categoryFields={state.categoryFields}
                  setCategoryFieldValue={state.setCategoryFieldValue}
                  categoryOptions={state.categories.filter(({ disabled }) => !disabled)}
                  loading={bulkLoading}
                  progress={bulk.progress}
                />
              </>
            )}
          </>
        )}
      </>
    );
  }
);

export default ProfilerSearch;
